import { useEffect, useRef, useState } from "react";
import type { LucideIcon } from "lucide-react";
import {
  Activity,
  Check,
  FolderKanban,
  Globe2,
  Mail,
  MapPinned,
  Search,
  ShieldCheck,
  Users,
  X,
} from "lucide-react";
import type { Site, SiteTargetAudience } from "@shared/types";

const EMPTY_TARGET_AUDIENCE: SiteTargetAudience = {
  age_ranges: [],
  gender_identities: [],
  household_income_ranges: [],
  occupations: [],
  education_levels: [],
  geographic_focus: [],
  languages: [],
  interests: [],
  notes: "",
};

const TARGET_AUDIENCE_SUGGESTIONS: Record<keyof SiteTargetAudience, string[]> =
  {
    age_ranges: ["18-24", "25-34", "35-44", "45-54", "55-64", "65+"],
    gender_identities: ["Women", "Men", "Non-binary", "Mixed household"],
    household_income_ranges: [
      "$50k-$75k",
      "$75k-$100k",
      "$100k-$150k",
      "$150k-$250k",
      "$250k+",
    ],
    occupations: [
      "Founders",
      "Marketing leaders",
      "Operations leaders",
      "Small business owners",
      "Executives",
    ],
    education_levels: [
      "High school",
      "Some college",
      "Bachelor's degree",
      "Master's degree",
      "Professional certification",
    ],
    geographic_focus: [
      "Pacific Northwest",
      "United States",
      "Major metro areas",
      "Remote-first teams",
    ],
    languages: ["English", "Spanish", "French", "German"],
    interests: [
      "Growth marketing",
      "SEO",
      "Lead generation",
      "Brand strategy",
      "Revenue operations",
    ],
    notes: [],
  };

function coerceAudienceList(value: unknown) {
  if (!Array.isArray(value)) {
    return [];
  }

  const seen = new Set<string>();
  const normalized: string[] = [];

  for (const item of value) {
    if (typeof item !== "string") {
      continue;
    }
    const trimmed = item.trim().replace(/\s+/g, " ");
    const key = trimmed.toLowerCase();
    if (!trimmed || seen.has(key)) {
      continue;
    }
    seen.add(key);
    normalized.push(trimmed);
  }

  return normalized;
}

function getTargetAudienceDraft(site: Site | null): SiteTargetAudience {
  const profile = site?.brand_config?.target_audience;

  if (!profile || typeof profile !== "object") {
    return { ...EMPTY_TARGET_AUDIENCE };
  }

  const audience = profile as Record<string, unknown>;

  return {
    age_ranges: coerceAudienceList(audience.age_ranges),
    gender_identities: coerceAudienceList(audience.gender_identities),
    household_income_ranges: coerceAudienceList(
      audience.household_income_ranges
    ),
    occupations: coerceAudienceList(audience.occupations),
    education_levels: coerceAudienceList(audience.education_levels),
    geographic_focus: coerceAudienceList(audience.geographic_focus),
    languages: coerceAudienceList(audience.languages),
    interests: coerceAudienceList(audience.interests),
    notes: typeof audience.notes === "string" ? audience.notes.trim() : "",
  };
}

function countPopulatedFields(targetAudience: SiteTargetAudience) {
  const values = Object.entries(targetAudience);
  let count = 0;

  for (const [, value] of values) {
    if (Array.isArray(value) && value.length > 0) {
      count += 1;
      continue;
    }

    if (typeof value === "string" && value.trim()) {
      count += 1;
    }
  }

  return count;
}

function TagField({
  label,
  helperText,
  icon: Icon,
  values,
  suggestions,
  disabled,
  onChange,
}: {
  label: string;
  helperText: string;
  icon: LucideIcon;
  values: string[];
  suggestions: string[];
  disabled: boolean;
  onChange: (values: string[]) => void;
}) {
  const [query, setQuery] = useState("");
  const normalizedQuery = query.trim().toLowerCase();

  function addValue(rawValue: string) {
    const trimmed = rawValue.trim().replace(/\s+/g, " ");
    if (!trimmed) {
      return;
    }

    if (values.some((value) => value.toLowerCase() === trimmed.toLowerCase())) {
      setQuery("");
      return;
    }

    onChange([...values, trimmed]);
    setQuery("");
  }

  function removeValue(valueToRemove: string) {
    onChange(values.filter((value) => value !== valueToRemove));
  }

  const filteredSuggestions = suggestions
    .filter((suggestion) => {
      if (!normalizedQuery) {
        return true;
      }

      return suggestion.toLowerCase().includes(normalizedQuery);
    })
    .filter(
      (suggestion) =>
        !values.some(
          (value) => value.toLowerCase() === suggestion.toLowerCase()
        )
    )
    .slice(0, 6);

  return (
    <section className="surface-card bg-white/[0.03]">
      <div className="flex items-start gap-3">
        <div className="rounded-2xl border border-white/8 bg-white/[0.04] p-3 text-neutral-300">
          <Icon className="h-5 w-5" />
        </div>
        <div>
          <p className="text-xs uppercase tracking-[0.22em] text-neutral-500">
            {label}
          </p>
          <p className="mt-2 text-sm leading-6 text-neutral-400">
            {helperText}
          </p>
        </div>
      </div>

      <div className="mt-4 flex min-h-12 flex-wrap gap-2">
        {values.length === 0 ? (
          <span className="rounded-full border border-dashed border-white/10 px-3 py-1.5 text-xs uppercase tracking-[0.18em] text-neutral-500">
            Nothing added yet
          </span>
        ) : (
          values.map((value) => (
            <span
              key={value}
              className="inline-flex items-center gap-2 rounded-full border border-lime-300/20 bg-lime-300/10 px-3 py-1.5 text-sm text-lime-50"
            >
              {value}
              {disabled ? null : (
                <button
                  type="button"
                  className="rounded-full bg-black/30 p-1 text-lime-100 transition hover:bg-black/50"
                  aria-label={`Remove ${value}`}
                  onClick={() => removeValue(value)}
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </span>
          ))
        )}
      </div>

      <label className="field-shell mt-4">
        <span className="field-label">{label}</span>
        <span className="field-addon">
          <Search className="h-4 w-4" />
        </span>
        <input
          className="field-input"
          placeholder="Type and press Enter"
          disabled={disabled}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={(event) => {
            if (!["Enter", ",", "Tab"].includes(event.key)) {
              return;
            }

            if (!query.trim()) {
              return;
            }

            event.preventDefault();
            addValue(query);
          }}
        />
      </label>

      {filteredSuggestions.length > 0 ? (
        <div className="mt-4 flex flex-wrap gap-2">
          {filteredSuggestions.map((suggestion) => (
            <button
              key={suggestion}
              type="button"
              className="rounded-full border border-white/8 bg-white/[0.03] px-3 py-1.5 text-sm text-neutral-200 transition hover:border-white/14 hover:bg-white/[0.06] disabled:opacity-50"
              disabled={disabled}
              onClick={() => addValue(suggestion)}
            >
              {suggestion}
            </button>
          ))}
        </div>
      ) : null}
    </section>
  );
}

export function TargetAudienceEditor({
  site,
  canEdit,
  onSave,
}: {
  site: Site | null;
  canEdit: boolean;
  onSave: (siteId: string, targetAudience: SiteTargetAudience) => Promise<void>;
}) {
  const [draft, setDraft] = useState<SiteTargetAudience>(() =>
    getTargetAudienceDraft(site)
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const notesRef = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    setDraft(getTargetAudienceDraft(site));
    setError(null);
    setSuccess(null);
  }, [site]);

  useEffect(() => {
    if (!notesRef.current) {
      return;
    }

    const element = notesRef.current;
    element.style.height = "0px";
    element.style.height = `${Math.max(element.scrollHeight, 112)}px`;
  }, [draft.notes]);

  if (!site) {
    return (
      <div className="surface-card">
        <p className="text-xs uppercase tracking-[0.22em] text-neutral-500">
          Target Audience
        </p>
        <p className="mt-3 text-lg font-semibold text-white">
          Select a single site
        </p>
        <p className="mt-2 max-w-2xl text-sm leading-6 text-neutral-400">
          Audience demographics belong to one site profile at a time. Pick a
          site from the selector in the left rail, then define the demographic
          groups you want strategy and execution to target.
        </p>
      </div>
    );
  }

  const activeSite = site;
  const populatedFieldCount = countPopulatedFields(draft);

  async function handleSave() {
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      await onSave(activeSite.id, draft);
      setSuccess("Audience profile saved.");
    } catch (saveError) {
      setError(
        saveError instanceof Error
          ? saveError.message
          : "Unable to save target audience"
      );
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-4">
      <section className="surface-card">
        <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_18rem]">
          <div>
            <p className="text-xs uppercase tracking-[0.22em] text-neutral-500">
              Target Audience
            </p>
            <h2 className="mt-3 text-2xl font-semibold text-white">
              Define the demographic profile for {activeSite.name}
            </h2>
            <p className="mt-3 max-w-3xl text-sm leading-6 text-neutral-400">
              Capture who this site is built to reach so future content,
              metadata, and execution decisions stay anchored to the right
              people.
            </p>
            {!canEdit ? (
              <div className="mt-4 rounded-2xl border border-white/8 bg-white/[0.03] px-4 py-3 text-sm text-neutral-300">
                This profile is view-only for your role.
              </div>
            ) : null}
          </div>

          <div className="surface-card bg-white/[0.03]">
            <p className="text-xs uppercase tracking-[0.18em] text-neutral-500">
              Profile status
            </p>
            <div className="mt-4 grid gap-3">
              <div className="rounded-2xl border border-white/6 bg-black/20 p-4">
                <p className="text-xs uppercase tracking-[0.18em] text-neutral-500">
                  Site
                </p>
                <p className="mt-2 text-sm font-medium text-white">
                  {activeSite.primary_url}
                </p>
              </div>
              <div className="rounded-2xl border border-white/6 bg-black/20 p-4">
                <p className="text-xs uppercase tracking-[0.18em] text-neutral-500">
                  Populated fields
                </p>
                <p className="mt-2 text-2xl font-semibold text-white">
                  {populatedFieldCount}
                </p>
              </div>
            </div>
          </div>
        </div>

        {error ? (
          <div className="mt-4 rounded-2xl border border-red-400/25 bg-red-500/10 px-4 py-3 text-sm text-red-100">
            {error}
          </div>
        ) : null}

        {success ? (
          <div className="mt-4 rounded-2xl border border-lime-300/20 bg-lime-300/10 px-4 py-3 text-sm text-lime-50">
            {success}
          </div>
        ) : null}

        <div className="mt-5 flex flex-wrap items-center gap-3">
          <button
            type="button"
            className="inline-flex items-center gap-2 rounded-2xl border border-lime-300/25 bg-lime-300/10 px-4 py-3 text-sm font-medium text-lime-100 transition hover:bg-lime-300/15 disabled:opacity-50"
            disabled={saving || !canEdit}
            onClick={() => void handleSave()}
          >
            {saving ? (
              <Check className="h-4 w-4" />
            ) : (
              <Users className="h-4 w-4" />
            )}
            {saving ? "Saving audience..." : "Save audience profile"}
          </button>
          <button
            type="button"
            className="inline-flex items-center gap-2 rounded-2xl border border-white/8 bg-white/[0.03] px-4 py-3 text-sm font-medium text-white transition hover:border-white/14 hover:bg-white/[0.05] disabled:opacity-50"
            disabled={saving}
            onClick={() => {
              setDraft(getTargetAudienceDraft(site));
              setError(null);
              setSuccess(null);
            }}
          >
            <X className="h-4 w-4" />
            Reset changes
          </button>
        </div>
      </section>

      <div className="grid gap-4 xl:grid-cols-2">
        <TagField
          label="Age Ranges"
          helperText="Which age groups best match the buying audience?"
          icon={Users}
          values={draft.age_ranges}
          suggestions={TARGET_AUDIENCE_SUGGESTIONS.age_ranges}
          disabled={!canEdit}
          onChange={(values) =>
            setDraft((current) => ({ ...current, age_ranges: values }))
          }
        />
        <TagField
          label="Gender Identities"
          helperText="Add gender segments only when the audience definition truly depends on them."
          icon={ShieldCheck}
          values={draft.gender_identities}
          suggestions={TARGET_AUDIENCE_SUGGESTIONS.gender_identities}
          disabled={!canEdit}
          onChange={(values) =>
            setDraft((current) => ({ ...current, gender_identities: values }))
          }
        />
        <TagField
          label="Household Income"
          helperText="Track the income bands this offer is usually priced for."
          icon={Activity}
          values={draft.household_income_ranges}
          suggestions={TARGET_AUDIENCE_SUGGESTIONS.household_income_ranges}
          disabled={!canEdit}
          onChange={(values) =>
            setDraft((current) => ({
              ...current,
              household_income_ranges: values,
            }))
          }
        />
        <TagField
          label="Occupations"
          helperText="List common job titles, roles, or buyer-side functions."
          icon={FolderKanban}
          values={draft.occupations}
          suggestions={TARGET_AUDIENCE_SUGGESTIONS.occupations}
          disabled={!canEdit}
          onChange={(values) =>
            setDraft((current) => ({ ...current, occupations: values }))
          }
        />
        <TagField
          label="Education Levels"
          helperText="Capture any education or credential markers that matter."
          icon={ShieldCheck}
          values={draft.education_levels}
          suggestions={TARGET_AUDIENCE_SUGGESTIONS.education_levels}
          disabled={!canEdit}
          onChange={(values) =>
            setDraft((current) => ({ ...current, education_levels: values }))
          }
        />
        <TagField
          label="Geographic Focus"
          helperText="Define the regions, markets, or locales this site is meant to serve."
          icon={MapPinned}
          values={draft.geographic_focus}
          suggestions={TARGET_AUDIENCE_SUGGESTIONS.geographic_focus}
          disabled={!canEdit}
          onChange={(values) =>
            setDraft((current) => ({ ...current, geographic_focus: values }))
          }
        />
        <TagField
          label="Languages"
          helperText="List the languages that matter for messaging and content."
          icon={Globe2}
          values={draft.languages}
          suggestions={TARGET_AUDIENCE_SUGGESTIONS.languages}
          disabled={!canEdit}
          onChange={(values) =>
            setDraft((current) => ({ ...current, languages: values }))
          }
        />
        <TagField
          label="Interests"
          helperText="Add topical interests that consistently map to the audience."
          icon={Search}
          values={draft.interests}
          suggestions={TARGET_AUDIENCE_SUGGESTIONS.interests}
          disabled={!canEdit}
          onChange={(values) =>
            setDraft((current) => ({ ...current, interests: values }))
          }
        />
      </div>

      <section className="surface-card bg-white/[0.03]">
        <p className="text-xs uppercase tracking-[0.22em] text-neutral-500">
          Audience Notes
        </p>
        <p className="mt-2 text-sm leading-6 text-neutral-400">
          Capture nuance that does not fit cleanly into the demographic lists.
        </p>

        <label className="field-shell mt-4 items-start overflow-hidden">
          <span className="field-label">Audience Notes</span>
          <span className="field-addon pt-4">
            <Mail className="h-4 w-4" />
          </span>
          <textarea
            ref={notesRef}
            className="field-input min-h-28 resize-none py-4 leading-6"
            placeholder="Ex. Owner-led service businesses in growth mode, typically in metro markets, with lean marketing teams and a need for measurable pipeline."
            disabled={!canEdit}
            rows={4}
            value={draft.notes}
            onChange={(event) =>
              setDraft((current) => ({
                ...current,
                notes: event.target.value,
              }))
            }
            onInput={(event) => {
              const element = event.currentTarget;
              element.style.height = "0px";
              element.style.height = `${Math.max(element.scrollHeight, 112)}px`;
            }}
          />
        </label>
      </section>
    </div>
  );
}
